import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import DashboardDateFilter from '../ui/DashboardDateFilter';
import StatusBadge from '../ui/StatusBadge';
import { getMySourcedProfiles } from '../../services/profileService';
import { getDecryptedData } from '../../utils/authStorage';
import { buildSourcedProfileDeepLink } from '../../utils/profileNavigation';
import { getDefaultDashboardDateRange } from '../../utils/requirementDateFilter';
import { getApiErrorMessage } from '../../utils/apiError';
import { useLayout } from '../../layout/AppLayout';

const DEFAULT_DATE_RANGE = getDefaultDashboardDateRange();

function MySourcedProfilesPanel({ embedded = false }) {
  const navigate = useNavigate();
  const userType = getDecryptedData('User Type') || 'User';
  const { tableRefresh } = useLayout();
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await getMySourcedProfiles(dateRange.from, dateRange.to);
      setProfiles(Array.isArray(data?.profiles) ? data.profiles : []);
    } catch (error) {
      console.error('Failed to load my sourced profiles:', error);
      setProfiles([]);
      setLoadError(getApiErrorMessage(error, 'Unable to load your sourced profiles. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [dateRange.from, dateRange.to]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles, tableRefresh.candidates]);

  const handleProfileClick = (profile) => {
    if (!profile?.requirementId || !profile?.profileId) return;
    const path = buildSourcedProfileDeepLink(userType, profile.requirementId, profile.profileId);
    navigate(path);
  };

  return (
    <section className={`hr-card${embedded ? ' hr-card--embedded' : ''}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h2 className="h5 mb-1">My Sourced Profiles</h2>
          <p className="hr-page-hint mb-0">
            Profiles you uploaded in the selected date range. Click a row to open the requirement and profile.
          </p>
        </div>
        <DashboardDateFilter
          onApply={setDateRange}
          onReset={() => setDateRange(DEFAULT_DATE_RANGE)}
        />
      </div>

      <p className="hr-dashboard-filter-hint mb-3">
        Showing uploads from
        {' '}
        {dateRange.from || '—'}
        {' '}
        to
        {' '}
        {dateRange.to || '—'}
      </p>

      {loading && (
        <p className="text-muted mb-0">Loading your sourced profiles…</p>
      )}

      {!loading && loadError && (
        <p className="text-danger mb-0">{loadError}</p>
      )}

      {!loading && !loadError && profiles.length === 0 && (
        <p className="hr-empty-state mb-0">
          No profiles sourced by you for the selected date range.
        </p>
      )}

      {!loading && !loadError && profiles.length > 0 && (
        <div className="table-responsive">
          <Table bordered hover responsive className="hr-table table-sm mb-0">
            <thead>
              <tr>
                <th>Client</th>
                <th>Role</th>
                <th>Candidate Name</th>
                <th>Status</th>
                <th>Phone</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr
                  key={profile.profileId}
                  className="hr-clickable-row"
                  onClick={() => handleProfileClick(profile)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleProfileClick(profile);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${profile.candidateName} in requirement list`}
                >
                  <td>{profile.client || '—'}</td>
                  <td>{profile.role || '—'}</td>
                  <td>{profile.candidateName || '—'}</td>
                  <td>
                    <StatusBadge status={profile.status} useStoredLabel />
                  </td>
                  <td>{profile.phone || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </section>
  );
}

export default MySourcedProfilesPanel;
